import { redirect } from "@tanstack/react-router";
import {
  isFeatureEnabled,
  isFeatureEnabledAt,
  isTrialAgent,
  NAVIGATION,
  navItem,
  type FeatureKey,
  type Permission,
  type PolicySubject,
  workspaceOfPath,
} from "@alm/policy";
import type { Me } from "@alm/api-contracts";
import { portalHomeFor } from "@alm/domain";
import { chosenFacility } from "@/lib/facility/facility-choice";
import { routeAllowed } from "@/lib/policy";
import { canHere } from "@/lib/policy/policy-here";

/** `beforeLoad` guard using the navigation registry's permission and feature. UX only, the API
 * refuses the data anyway. An anonymous caller is left to the layout. */
export function guardRoute(me: Me | null, feature: FeatureKey): void {
  if (!me || isPortalLogin(me)) return;
  if (routeAllowed(me, navItem(feature))) return;
  lockedAgentRedirect(me, feature);
  // The dashboard is where refusals land, so it must not redirect to itself.
  const home = feature === "dashboard" ? firstAllowedPath(me) : "/";
  if (home) throw redirect({ to: home });
}

/** A screen of an AI employee the organization has at no building opens that employee's lock, so a
 * link or a typed address says why instead of landing on the dashboard. */
function lockedAgentRedirect(me: Me, feature: FeatureKey): void {
  const subject = me.subject as PolicySubject;
  const somewhere =
    isFeatureEnabled(subject, feature) ||
    me.buildings.some((building) => isFeatureEnabledAt(subject, feature, building.id));
  if (somewhere) return;
  const path = NAVIGATION.find((entry) => entry.id === feature)?.path;
  const agent = path ? workspaceOfPath(path).id : null;
  if (agent && isTrialAgent(agent)) throw redirect({ to: "/agent-trial", search: { agent } });
}

/** The first screen this login may open, in registry order. */
export function firstAllowedPath(me: Me): string | null {
  return (
    NAVIGATION.find((entry) => entry.id !== "dashboard" && routeAllowed(me, entry))?.path ?? null
  );
}

/** For a screen one permission opens and no menu entry names: asked at the building chosen. A
 * refused person lands on `to`. */
export function guardPermission(me: Me | null, permission: Permission, to = "/"): void {
  if (!me || isPortalLogin(me)) return;
  const subject = me.subject as PolicySubject;
  if (canHere(subject, permission, chosenFacility(subject))) return;
  throw redirect({ to });
}

/** The module at the building chosen, or at any building when all are in view: a building can
 * switch a module off or on for itself. */
export function guardFeature(me: Me | null, feature: FeatureKey): void {
  if (!me || isPortalLogin(me)) return;
  const subject = me.subject as PolicySubject;
  const here = subject.organization ? chosenFacility(subject) : null;
  if (subject.organization && isFeatureEnabledAt(subject, feature, here)) return;
  lockedAgentRedirect(me, feature);
  throw redirect({ to: "/" });
}

/** For the reader's own record across every building: the API asks no permission, so the
 * organization's module switch answers. */
export function guardOwnFeature(me: Me | null, feature: FeatureKey): void {
  if (!me || isPortalLogin(me)) return;
  const subject = me.subject as PolicySubject;
  if (subject.organization && isFeatureEnabled(subject, feature)) return;
  throw redirect({ to: "/" });
}

// Portal logins are sent home by the layout; refusing them here would loop through `/`.
function isPortalLogin(me: Me): boolean {
  return portalHomeFor(me.user.role) !== null;
}
